import React, { useState, useCallback } from 'react';
import { View, StyleSheet, FlatList, RefreshControl } from 'react-native';
import { Text, Card, ActivityIndicator, Divider } from 'react-native-paper';
import { useFocusEffect } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import api from '../../services/api';

const BajoStockScreen = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.get('/productos/bajo-stock/reporte');
      setProductos(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => { loadData(); }, [loadData]));

  const getStockColor = (stock, minimo) => {
    if (stock <= 0) return '#DC3545';
    if (stock <= minimo * 0.5) return '#FD7E14';
    return '#FFC107';
  };

  const renderItem = ({ item }) => {
    const stock = parseInt(item.stock_total) || 0;
    const minimo = parseInt(item.minimo) || 10;
    const color = getStockColor(stock, minimo);
    const porcentaje = minimo > 0 ? Math.min(Math.round((stock / minimo) * 100), 100) : 0;

    return (
      <Card style={[styles.card, { borderLeftWidth: 4, borderLeftColor: color }]}>
        <Card.Content>
          <View style={styles.itemRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.nombre}>{item.nombre}</Text>
              <View style={{ flexDirection: 'row', marginTop: 2 }}>
                {item.marca && <Text style={styles.marca}>{item.marca}</Text>}
                <Text style={styles.codigo}>{item.codigo}</Text>
              </View>
              {item.categoria_nombre && (
                <Text style={styles.categoria}>{item.categoria_nombre}</Text>
              )}
            </View>
            <View style={styles.stockContainer}>
              <Text style={[styles.stockActual, { color }]}>{stock}</Text>
              <Text style={styles.stockMinimo}>/ {minimo} min</Text>
              {item.ventas_ultimo_mes > 0 && (
                <Text style={styles.ventasMes}>{item.ventas_ultimo_mes} vendidos/mes</Text>
              )}
            </View>
          </View>

          <View style={styles.barContainer}>
            <View style={styles.barBg}>
              <View style={[styles.barFill, { width: `${porcentaje}%`, backgroundColor: color }]} />
            </View>
            <Text style={[styles.porcentaje, { color }]}>{porcentaje}%</Text>
          </View>

          {stock <= 0 && (
            <View style={styles.alertaContainer}>
              <Ionicons name="alert-circle" size={16} color="#DC3545" />
              <Text style={styles.alertaText}>SIN STOCK - Reabastecimiento urgente</Text>
            </View>
          )}
        </Card.Content>
      </Card>
    );
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#DC3545" />
        <Text style={{ marginTop: 10, color: '#666' }}>Analizando stock...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Card style={styles.resumenCard}>
        <Card.Content style={styles.resumenContent}>
          <Ionicons name="alert-circle" size={24} color="#DC3545" />
          <View style={{ marginLeft: 12 }}>
            <Text style={styles.resumenTitle}>{productos.length} productos bajo stock minimo</Text>
            <Text style={styles.resumenSub}>
              {productos.filter(p => (parseInt(p.stock_total) || 0) <= 0).length} sin stock
            </Text>
          </View>
        </Card.Content>
      </Card>

      <FlatList
        data={productos}
        keyExtractor={item => item.id.toString()}
        renderItem={renderItem}
        contentContainerStyle={styles.listContainer}
        ItemSeparatorComponent={() => <View style={{ height: 8 }} />}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); loadData(); }} />}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons name="checkmark-circle-outline" size={64} color="#28A745" />
            <Text style={[styles.emptyText, { color: '#28A745' }]}>Todos los productos tienen stock suficiente</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f5f5f5' },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  resumenCard: { margin: 8, backgroundColor: '#FFF3F3', elevation: 2 },
  resumenContent: { flexDirection: 'row', alignItems: 'center' },
  resumenTitle: { fontSize: 16, fontWeight: 'bold', color: '#DC3545' },
  resumenSub: { fontSize: 13, color: '#666', marginTop: 2 },
  listContainer: { padding: 8 },
  card: { elevation: 2 },
  itemRow: { flexDirection: 'row', justifyContent: 'space-between' },
  nombre: { fontSize: 15, fontWeight: 'bold', color: '#333' },
  marca: { fontSize: 12, color: '#17A2B8', marginRight: 8 },
  codigo: { fontSize: 12, color: '#999' },
  categoria: { fontSize: 11, color: '#666', marginTop: 2 },
  stockContainer: { alignItems: 'flex-end' },
  stockActual: { fontSize: 22, fontWeight: 'bold' },
  stockMinimo: { fontSize: 11, color: '#888' },
  ventasMes: { fontSize: 10, color: '#6F42C1', marginTop: 2 },
  barContainer: { flexDirection: 'row', alignItems: 'center', marginTop: 8 },
  barBg: { flex: 1, height: 6, backgroundColor: '#E9ECEF', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 3 },
  porcentaje: { fontSize: 12, fontWeight: 'bold', marginLeft: 8, minWidth: 35 },
  alertaContainer: { flexDirection: 'row', alignItems: 'center', marginTop: 8, backgroundColor: '#FFF3F3', padding: 6, borderRadius: 4 },
  alertaText: { fontSize: 11, color: '#DC3545', fontWeight: 'bold', marginLeft: 4 },
  emptyContainer: { justifyContent: 'center', alignItems: 'center', paddingVertical: 50 },
  emptyText: { marginTop: 10, fontSize: 16 },
});

export default BajoStockScreen;
